import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection, query, doc, getDoc, deleteField, onSnapshot, Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { setDoc, updateDoc, deleteDoc } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { PerfilUsuario, RolUsuario } from '../types';
import { MODULOS_POR_DEFECTO } from '../config/modulos';
import { ConfigPlus, EstadoPlus, calcularPlus } from '../config/planes';

/**
 * Perfil y permisos del usuario que ha entrado.
 *
 * El documento `usuarios/{uid}` se crea solo en el primer acceso, con los
 * módulos de MODULOS_POR_DEFECTO (Finanzas, A medias y Tareas); el resto los activa un
 * administrador. Una cuenta sin ninguno (porque se los quitaron) ve la pantalla
 * de cuenta pendiente. Esto es solo la mitad visible del
 * asunto; quien impide de verdad usar un módulo sin permiso son las reglas de
 * Firestore, no este contexto.
 *
 * El primer administrador se nombra a mano desde la consola de Firebase, que
 * escribe saltándose las reglas. Desde ahí ya se pueden nombrar otros.
 */
interface PerfilContextType {
  perfil: PerfilUsuario | null;
  cargandoPerfil: boolean;
  esAdmin: boolean;
  tieneModulo: (idModulo: string) => boolean;
  /** Algún módulo activado: si no, la cuenta está pendiente de activación. */
  tieneAlgunModulo: boolean;

  /** myarinn Plus (src/config/planes.ts). */
  esPlus: boolean;
  estadoPlus: EstadoPlus;
  /** Cuándo empiezan los límites; null = todavía no se aplican. */
  configPlus: ConfigPlus | null;
  /** Préstamos es de Plus. Sin él (o sin el permiso antiguo del administrador) queda en solo lectura. */
  puedeEscribirPrestamos: boolean;

  /** Todos los perfiles. Solo se suscribe si quien mira es administrador. */
  usuarios: PerfilUsuario[];
  cargandoUsuarios: boolean;
  /** Cuentas que han entrado pero no tienen ningún módulo. 0 si no eres admin. */
  pendientesDeActivar: number;
  cambiarModulo: (uid: string, idModulo: string, activo: boolean) => Promise<void>;
  cambiarRol: (uid: string, rol: RolUsuario) => Promise<void>;
  /** Plus de por vida regalado por un administrador (planOrigen 'admin'). */
  regalarPlus: (uid: string, activo: boolean) => Promise<void>;
  /** Activa los límites desde una fecha (con el regalo de MESES_REGALO), o los quita con null. */
  guardarConfigPlus: (config: ConfigPlus | null) => Promise<void>;
  /** Recuerda que ya se ofreció la plantilla de categorías, para no repetirla. */
  marcarPlantillaCategoriasVista: () => Promise<void>;
  /** Cierra para siempre el aviso de una novedad. */
  marcarNovedadVista: (id: string) => Promise<void>;
}

const PerfilContext = createContext<PerfilContextType | undefined>(undefined);

export const PerfilProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: cargandoAuth } = useAuth();

  const [perfil, setPerfil] = useState<PerfilUsuario | null>(null);
  const [cargandoPerfil, setCargandoPerfil] = useState(true);
  const [usuarios, setUsuarios] = useState<PerfilUsuario[]>([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  const [configPlus, setConfigPlus] = useState<ConfigPlus | null>(null);

  // ── Perfil propio: crear si no existe, y escuchar cambios ────────────────
  useEffect(() => {
    if (cargandoAuth) return;
    if (!user) {
      setPerfil(null);
      setUsuarios([]);
      setCargandoPerfil(false);
      return;
    }

    setCargandoPerfil(true);
    const ref = doc(db, 'usuarios', user.uid);
    let vivo = true;

    // Primero nos aseguramos de que el documento existe; si no, lo creamos.
    // Sin esto, quien ya tenía cuenta antes de esta versión no tendría perfil.
    const asegurar = async () => {
      try {
        const snap = await getDoc(ref);
        if (!vivo) return;
        if (!snap.exists()) {
          await setDoc(ref, {
            email: user.email ?? '',
            nombre: user.displayName ?? '',
            rol: 'usuario',
            // Cuenta nueva: solo los módulos por defecto (las reglas no dejan más)
            modulos: Object.fromEntries(MODULOS_POR_DEFECTO.map(m => [m, true])),
            // Hora del servidor: las reglas lo exigen, porque de esta fecha
            // depende el Plus regalado a quien ya estaba al activar los límites.
            createdAt: serverTimestamp(),
            ultimoAcceso: Timestamp.now(),
          });
        } else {
          // Refrescar el email por si lo cambió, y dejar constancia del acceso.
          // El nombre, solo si faltaba: con Apple llega un instante después de
          // crear el perfil, y se habría quedado vacío.
          await updateDoc(ref, {
            email: user.email ?? snap.data().email ?? '',
            ...(!snap.data().nombre && user.displayName ? { nombre: user.displayName } : {}),
            ultimoAcceso: Timestamp.now(),
          });
          // Tareas llegó cuando ya tenías cuenta: se activa sola, una vez. Si un
          // administrador la apagó (false), se respeta. Las reglas lo permiten
          // solo así, en una escritura aparte (seActivaTareas).
          const modulos = snap.data().modulos ?? {};
          if (snap.data().rol !== 'admin' && !('tareas' in modulos) && Object.values(modulos).some(Boolean)) {
            await updateDoc(ref, { 'modulos.tareas': true })
              .catch(e => console.warn('No se pudo activar Tareas', e));
          }
        }
      } catch (e) {
        // Si las reglas lo rechazan no hay nada que hacer aquí: el listener
        // de abajo dejará el perfil a null y se verá la cuenta pendiente.
        console.error('No se pudo preparar el perfil de usuario', e);
      }
    };

    asegurar();

    // Solo cuenta lo que el servidor ya ha confirmado. Si el propio usuario se
    // activa un módulo (al unirse a un plan de A medias), Firestore avisa antes
    // de guardarlo; los contextos se suscribirían con un permiso que el
    // servidor aún no ve, y la lista llegaría vacía. Por eso se ignoran los
    // cambios pendientes y se espera a la confirmación (includeMetadataChanges).
    const unsub = onSnapshot(ref, { includeMetadataChanges: true }, snap => {
      if (snap.metadata.hasPendingWrites) return;
      if (!snap.exists()) {
        setPerfil(null);
        setCargandoPerfil(false);
        return;
      }
      const d = snap.data();
      setPerfil({
        id: snap.id,
        email: d.email ?? '',
        nombre: d.nombre || undefined,
        rol: d.rol === 'admin' ? 'admin' : 'usuario',
        modulos: d.modulos ?? {},
        plantillaCategoriasVista: d.plantillaCategoriasVista === true,
        novedadesVistas: d.novedadesVistas ?? {},
        preferencias: d.preferencias ?? {},
        planHasta: d.planHasta?.toDate() ?? undefined,
        planVitalicio: d.planVitalicio === true,
        planOrigen: d.planOrigen ?? undefined,
        createdAt: d.createdAt?.toDate() ?? new Date(),
        ultimoAcceso: d.ultimoAcceso?.toDate() ?? new Date(),
      });
      setCargandoPerfil(false);
    }, err => {
      console.error('No se pudo leer el perfil', err);
      setPerfil(null);
      setCargandoPerfil(false);
    });

    return () => { vivo = false; unsub(); };
  }, [user, cargandoAuth]);

  const esAdmin = perfil?.rol === 'admin';

  // ── Cuándo empiezan los límites de Plus (lo mismo para todos) ─────────────
  useEffect(() => {
    if (!user) { setConfigPlus(null); return; }
    return onSnapshot(doc(db, 'configuracionGlobal', 'plus'), snap => {
      const d = snap.data();
      setConfigPlus(d?.limitesDesde && d?.regaloHasta
        ? { limitesDesde: d.limitesDesde.toDate(), regaloHasta: d.regaloHasta.toDate() }
        : null);
    }, err => {
      console.error('No se pudo leer la configuración de Plus', err);
      setConfigPlus(null);
    });
  }, [user]);

  // ── Listado completo: solo para administradores ──────────────────────────
  useEffect(() => {
    if (!esAdmin) {
      setUsuarios([]);
      setCargandoUsuarios(false);
      return;
    }

    setCargandoUsuarios(true);
    const unsub = onSnapshot(query(collection(db, 'usuarios')), snap => {
      const lista = snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          email: x.email ?? '',
          nombre: x.nombre || undefined,
          rol: x.rol === 'admin' ? 'admin' : 'usuario',
          modulos: x.modulos ?? {},
          planHasta: x.planHasta?.toDate() ?? undefined,
          planVitalicio: x.planVitalicio === true,
          planOrigen: x.planOrigen ?? undefined,
          createdAt: x.createdAt?.toDate() ?? new Date(),
          ultimoAcceso: x.ultimoAcceso?.toDate() ?? new Date(),
        } as PerfilUsuario;
      });
      lista.sort((a, b) => b.ultimoAcceso.getTime() - a.ultimoAcceso.getTime());
      setUsuarios(lista);
      setCargandoUsuarios(false);
    }, err => {
      console.error('No se pudo listar usuarios', err);
      setUsuarios([]);
      setCargandoUsuarios(false);
    });

    return unsub;
  }, [esAdmin]);

  const pendientesDeActivar = usuarios.filter(
    u => u.rol !== 'admin' && !Object.values(u.modulos ?? {}).some(Boolean),
  ).length;

  const tieneModulo = useCallback((idModulo: string) => {
    if (!perfil) return false;
    // Un administrador entra en todo: es la red de seguridad que impide
    // quedarse fuera de la propia aplicación.
    if (perfil.rol === 'admin') return true;
    // Préstamos lo ve todo el mundo: sin Plus, en solo lectura y con la oferta.
    if (idModulo === 'prestamos') return true;
    return perfil.modulos?.[idModulo] === true;
  }, [perfil]);

  const estadoPlus: EstadoPlus = perfil
    ? calcularPlus(perfil, configPlus)
    : { esPlus: false, motivo: null };
  const esPlus = estadoPlus.esPlus;
  // modulos.prestamos es el permiso de antes de Plus: quien lo tenía lo conserva.
  const puedeEscribirPrestamos = esPlus || perfil?.modulos?.prestamos === true;

  const tieneAlgunModulo = Boolean(
    perfil && (perfil.rol === 'admin' || Object.values(perfil.modulos ?? {}).some(Boolean)),
  );

  const cambiarModulo = async (uid: string, idModulo: string, activo: boolean) => {
    await updateDoc(doc(db, 'usuarios', uid), { [`modulos.${idModulo}`]: activo });
  };

  const cambiarRol = async (uid: string, rol: RolUsuario) => {
    await updateDoc(doc(db, 'usuarios', uid), { rol });
  };

  const regalarPlus = async (uid: string, activo: boolean) => {
    await updateDoc(doc(db, 'usuarios', uid), activo
      ? { planVitalicio: true, planOrigen: 'admin', planActualizado: Timestamp.now() }
      : { planVitalicio: deleteField(), planOrigen: deleteField(), planActualizado: Timestamp.now() });
  };

  const guardarConfigPlus = async (config: ConfigPlus | null) => {
    const ref = doc(db, 'configuracionGlobal', 'plus');
    if (!config) { await deleteDoc(ref); return; }
    await setDoc(ref, {
      limitesDesde: Timestamp.fromDate(config.limitesDesde),
      regaloHasta: Timestamp.fromDate(config.regaloHasta),
    });
  };

  // Va en el perfil y no en localStorage para que valga en cualquier dispositivo.
  // Las reglas lo permiten: el usuario puede tocar su perfil salvo rol y módulos.
  const marcarPlantillaCategoriasVista = useCallback(async () => {
    if (!user) return;
    await updateDoc(doc(db, 'usuarios', user.uid), { plantillaCategoriasVista: true });
  }, [user]);

  const marcarNovedadVista = useCallback(async (id: string) => {
    if (!user) return;
    await updateDoc(doc(db, 'usuarios', user.uid), { [`novedadesVistas.${id}`]: true });
  }, [user]);

  return (
    <PerfilContext.Provider value={{
      perfil, cargandoPerfil, esAdmin, tieneModulo, tieneAlgunModulo,
      esPlus, estadoPlus, configPlus, puedeEscribirPrestamos,
      usuarios, cargandoUsuarios, pendientesDeActivar, cambiarModulo, cambiarRol,
      regalarPlus, guardarConfigPlus,
      marcarPlantillaCategoriasVista, marcarNovedadVista,
    }}>
      {children}
    </PerfilContext.Provider>
  );
};

export const usePerfil = () => {
  const ctx = useContext(PerfilContext);
  if (!ctx) throw new Error('usePerfil debe usarse dentro de PerfilProvider');
  return ctx;
};
